import React, { useState, useEffect } from "react";
import { useTable } from "react-table";
import { db } from "../Firebase"; // Import Firestore instance from Firebase
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import "./Admin.css";

function Admin() {
  const [data, setData] = useState([]);
  const [newProperties, setNewProperties] = useState([]); // Local state for new properties
  const [saveSuccess, setSaveSuccess] = useState(false); // State for showing success notification

  useEffect(() => {
    // Fetch properties from Firebase when the component mounts
    const fetchProperties = async () => {
      const querySnapshot = await getDocs(collection(db, "properties"));
      const properties = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setData(properties);
    };

    fetchProperties();
  }, []);

  // Add property handler (only adds an empty row locally)
  const addProperty = () => {
    const newProperty = {
      houseNumber: "",
      roadName: "",
      town: "Marlow",
      county: "Buckinghamshire",
      postcode: "",
      askingPrice: 0,
      agent: "",
      weeksOnMarket: 0,
      rightmoveLink: "",
      notes: "",
    };
    setNewProperties([...newProperties, newProperty]);
    setSaveSuccess(false); // Reset success notification when adding a new property
  };

  // Save new properties to Firestore
  const saveProperties = async () => {
    try {
      for (const property of newProperties) {
        const docRef = await addDoc(collection(db, "properties"), property);
        setData((oldData) => [...oldData, { id: docRef.id, ...property }]);
      }
      // Clear new properties after saving
      setNewProperties([]);
      // Show success notification
      setSaveSuccess(true);
    } catch (error) {
      console.error("Error saving properties: ", error);
      alert("Failed to save properties: " + error.message);
    }
  };

  // Update property handler (for local updates and Firestore updates)
  const updateData = async (rowIndex, columnId, value) => {
    let updatedData;
    if (rowIndex < data.length) {
      updatedData = data.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      });
      setData(updatedData);

      // Update the property in Firestore
      const propertyToUpdate = updatedData[rowIndex];
      if (propertyToUpdate.id) {
        const propertyRef = doc(db, "properties", propertyToUpdate.id);
        await updateDoc(propertyRef, { [columnId]: value });
      }
    } else {
      updatedData = newProperties.map((row, index) => {
        if (index === rowIndex - data.length) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      });
      setNewProperties(updatedData);
    }
  };

  // Delete property handler
  const deleteProperty = async (propertyId) => {
    try {
      await deleteDoc(doc(db, "properties", propertyId));
      setData((oldData) =>
        oldData.filter((property) => property.id !== propertyId)
      );
    } catch (error) {
      console.error("Error deleting property: ", error);
      alert("Failed to delete property: " + error.message);
    }
  };

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "House Name/Number",
        accessor: "houseNumber",
        Cell: EditableCell,
      },
      {
        Header: "Road Name",
        accessor: "roadName",
        Cell: EditableCell,
      },
      {
        Header: "Town",
        accessor: "town",
        Cell: EditableCell,
      },
      {
        Header: "County",
        accessor: "county",
        Cell: EditableCell,
      },
      {
        Header: "Postcode",
        accessor: "postcode",
        Cell: EditableCell,
      },
      {
        Header: "Asking Price",
        accessor: "askingPrice",
        Cell: EditableCell,
      },
      {
        Header: "Agent",
        accessor: "agent",
        Cell: EditableCell,
      },
      {
        Header: "Weeks on Market",
        accessor: "weeksOnMarket",
        Cell: EditableCell,
      },
      {
        Header: "Rightmove Link",
        accessor: "rightmoveLink",
        Cell: ({ value }) => (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View Listing
          </a>
        ),
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: EditableCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <button
            onClick={() => deleteProperty(row.original.id)}
            className="delete-property-button"
          >
            Delete
          </button>
        ),
      },
    ],
    [data]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: [...data, ...newProperties], // Display both saved and new properties
      defaultColumn: {
        Cell: EditableCell,
      },
      updateData,
    });

  // Editable cell rendering
  function EditableCell({
    value: initialValue,
    row: { index },
    column: { id },
    updateData,
  }) {
    const [value, setValue] = useState(initialValue);

    const onChange = (e) => {
      setValue(e.target.value);
    };

    const onBlur = () => {
      if (value !== initialValue) {
        updateData(index, id, value);
      }
    };

    return <input value={value} onChange={onChange} onBlur={onBlur} />;
  }

  return (
    <div>
      <h1>Admin Page</h1>
      <p>This page is for admin users to manage property data.</p>
      <button onClick={addProperty} className="add-property-button">
        Add Property
      </button>
      <button onClick={saveProperties} className="save-property-button">
        Save Properties
      </button>
      {saveSuccess && (
        <p className="success-message">Properties saved successfully!</p>
      )}
      <table {...getTableProps()} className="property-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Admin;
